import type { FunctionComponent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { ActionIcon, Stack, Text, Divider, Flex, Space, Tooltip } from '@mantine/core';

import { isInputGroupValue } from 'helpers/menuPreview';
import type { ModifierGroupFormValue, PreviewModifier } from 'helpers/menuPreview';

import Counter from 'primitives/Counter/Counter';

import { PlusIconSm } from '@chownow/pantry/icons';
import { ModifierPrice } from './ModifierPrice';
import { NestedModifierIndicator } from './NestedModifierIndicator';

type GroupInputItemProps = {
  onModifierChange: (
    groupKey: string,
    modifiedValues: Record<string, { quantity: number; price: number }>,
  ) => void;
  onDrillIn: (modifierKey: string, modifierName: string) => void;
  modifiers: PreviewModifier[];
  groupKey: string;
  groupValue?: ModifierGroupFormValue;
  individualMaximum?: number;
  showTrailingDivider?: boolean;
};

export const GroupInputItem: FunctionComponent<GroupInputItemProps> = ({
  onModifierChange,
  onDrillIn,
  modifiers,
  groupKey,
  groupValue,
  individualMaximum,
  showTrailingDivider = true,
}) => {
  const initialInputValue = groupValue && isInputGroupValue(groupValue) ? groupValue : undefined;
  const [values, setValues] = useState<Record<string, number>>(
    modifiers.reduce<Record<string, number>>(
      (prev, modifier) => ({
        ...prev,
        [modifier.key]: initialInputValue?.[modifier.key]?.quantity ?? 0,
      }),
      {},
    ),
  );

  const handleInputChange = (key: string, value: number) => {
    setValues(prevValues => ({ ...prevValues, [key]: value }));
  };

  const previousValuesRef = useRef(values);

  useEffect(() => {
    const modifiedValues = modifiers.reduce<Record<string, { quantity: number; price: number }>>(
      (acc, modifier) => ({
        ...acc,
        [modifier.key]: { quantity: values[modifier.key], price: modifier.price ?? 0 },
      }),
      {},
    );
    onModifierChange(groupKey, modifiedValues);

    const newlyPositive = modifiers.find(
      modifier =>
        modifier.hasNestedGroups &&
        (values[modifier.key] ?? 0) > 0 &&
        (previousValuesRef.current[modifier.key] ?? 0) === 0,
    );
    previousValuesRef.current = values;
    if (newlyPositive) {
      onDrillIn(newlyPositive.key, newlyPositive.name ?? '');
    }
  }, [values, onModifierChange, onDrillIn, modifiers, groupKey]);

  return (
    <>
      <Stack gap="md">
        {modifiers.map(modifier => {
          const quantity = values[modifier.key] ?? 0;
          const increment = () => {
            setValues(prevValues => ({
              ...prevValues,
              [modifier.key]: Math.min(
                (prevValues[modifier.key] ?? 0) + 1,
                individualMaximum ?? 99,
              ),
            }));
          };
          const decrement = () => {
            setValues(prevValues => ({
              ...prevValues,
              [modifier.key]: Math.max((prevValues[modifier.key] ?? 0) - 1, 0),
            }));
          };

          return (
            <div key={modifier.key}>
              <Flex justify="space-between" align="center" gap="xs">
                <Stack gap={0} style={{ flexGrow: 1 }}>
                  {modifier.paused ? (
                    <Tooltip label="This modifier is unavailable">
                      <Text c="var(--content-secondary)">{modifier.name}</Text>
                    </Tooltip>
                  ) : (
                    <Text>{modifier.name}</Text>
                  )}
                  <ModifierPrice modifier={modifier} />
                </Stack>
                <Flex align="center" gap="xs">
                  {quantity === 0 ? (
                    <ActionIcon
                      onClick={increment}
                      radius="xl"
                      w={24}
                      h={24}
                      bg="var(--surface-secondary)"
                      aria-label={modifier.name}
                    >
                      <PlusIconSm />
                    </ActionIcon>
                  ) : (
                    <Counter
                      quantity={quantity}
                      increment={increment}
                      decrement={decrement}
                      set={(value: number) => handleInputChange(modifier.key, value ?? 0)}
                      size="sm"
                      variant="outlined"
                      minQuantity={0}
                      maxQuantity={individualMaximum ?? 99}
                    />
                  )}
                  <NestedModifierIndicator
                    variant="inline"
                    hasNestedGroups={modifier.hasNestedGroups ?? false}
                    isSelected={quantity > 0}
                    onDrillIn={() => onDrillIn(modifier.key, modifier.name ?? '')}
                  />
                </Flex>
              </Flex>
              <NestedModifierIndicator
                variant="banner"
                hasNestedGroups={modifier.hasNestedGroups ?? false}
                isSelected={quantity > 0}
                nestedSummary={modifier.nestedSummary}
                onDrillIn={() => onDrillIn(modifier.key, modifier.name ?? '')}
              />
            </div>
          );
        })}
      </Stack>
      {showTrailingDivider ? <Divider my="md" /> : <Space h="xl" />}
    </>
  );
};
